import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Hand, Check } from 'lucide-react'
import { getStopScreens, publishStopScreen, unpublishStopScreen } from '@meno/ui/lib/api'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@meno/ui/components/ui/dialog'
import { playlistThumbUrl } from '@/lib/playlistThumb'
import type { PlaylistMediaFile } from '@/components/PlaylistDialog'
import ChannelCard from './ChannelCard'

// Self-contained "Stop Screen" channel — one of the collapsed ChannelCards on
// the screen detail page's preview column. A stop screen is an overlay LAYER
// (publish is non-destructive), so assigning one here never disturbs the
// screen's normal content. It reads the org's stop screens and finds the one
// whose `screens` list contains this screen — no new endpoint needed. The row
// shows the current assignment; "Change" opens a grid picker (consistent with
// the content picker) with a "None" tile to clear, and the card carries a
// remove-X when one is assigned.
type StopScreenRow = { id: number; name: string; media?: PlaylistMediaFile[]; screens: { id: number }[] }

export default function StopScreenChannel({ screenId }: { screenId: number }) {
  const { t } = useTranslation()
  const [all, setAll] = useState<StopScreenRow[]>([])
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)

  const load = useCallback(() => {
    getStopScreens().then(r => setAll(r.data)).catch(() => {})
  }, [])
  useEffect(() => { load() }, [load])

  const assigned = all.find(s => s.screens?.some(x => x.id === screenId)) ?? null

  const assign = async (ss: StopScreenRow) => {
    if (busy) return
    setBusy(true)
    try {
      // One stop screen per screen — clear the previous assignment first.
      if (assigned && assigned.id !== ss.id) {
        await unpublishStopScreen(assigned.id, { screen_ids: [screenId] })
      }
      await publishStopScreen(ss.id, { screen_ids: [screenId] })
      toast.success(t('screens.stopScreenAssigned', 'Stop screen assigned'))
      load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.updateFailed', 'Failed to update'))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (busy || !assigned) return
    setBusy(true)
    try {
      await unpublishStopScreen(assigned.id, { screen_ids: [screenId] })
      toast.success(t('screens.stopScreenRemoved', 'Stop screen removed'))
      load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.updateFailed', 'Failed to update'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ChannelCard
        icon={Hand}
        title={t('screens.stopScreen', 'Stop Screen')}
        summary={assigned ? assigned.name : t('screens.noStopScreen', 'None')}
        changeLabel={assigned ? t('common.change', 'Change') : t('screens.addContent', 'Add')}
        onChange={() => setOpen(true)}
        onRemove={assigned ? remove : undefined}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('screens.stopScreen', 'Stop Screen')}</DialogTitle>
          </DialogHeader>

          {all.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              {t('screens.noStopScreensYet', 'Create one in Content Hub → Stop Screens')}
            </p>
          ) : (
            // Grid of tiles (consistent with the content picker) + a "None" tile
            // that clears the assignment.
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              <StopTile
                label={t('screens.noStopScreen', 'None')}
                selected={!assigned}
                disabled={busy}
                onClick={remove}
              />
              {all.map(ss => (
                <StopTile
                  key={ss.id}
                  label={ss.name}
                  thumb={playlistThumbUrl(ss.media ?? [])}
                  selected={assigned?.id === ss.id}
                  disabled={busy}
                  onClick={() => assign(ss)}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

// One selectable tile in the stop-screen grid — mirrors the content picker's
// AssetThumb (aspect-video preview + selected ring + name).
function StopTile({
  label, thumb, selected, disabled, onClick,
}: {
  label: string
  thumb?: string | null
  selected: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`group relative rounded-xl overflow-hidden border-2 text-start transition-all disabled:opacity-50 ${
        selected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-primary/40'
      }`}
    >
      <div className="block w-full aspect-video bg-stop-screen/10 overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={label} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Hand className="w-7 h-7 text-stop-screen" />
          </div>
        )}
      </div>
      {selected && (
        <span className="absolute top-1.5 end-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          <Check className="w-3.5 h-3.5" />
        </span>
      )}
      <p className="px-2 py-1.5 text-xs font-medium truncate">{label}</p>
    </button>
  )
}
